"use client";
import { useState, useEffect, useCallback } from "react";
import {
  getDatabase,
  ref,
  update,
  set,
  onValue,
  get,
  push,
  serverTimestamp,
  increment,
} from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
} from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../Firebase/firestore";
import {
  FaTruck,
  FaPencilAlt,
  FaTimes,
  FaInfoCircle,
  FaEye,
  FaCheckCircle,
  FaBan,
} from "react-icons/fa";
import InfoIcon from "../infoIcon";
import { useCount } from "../contexts/count-context";
import "./userOrders.css";

const UserOrders = ({ adminView = false, selectedBrand = "" }) => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [updatedOrders, setUpdatedOrders] = useState({});
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [brand, setBrand] = useState(
    adminView ? selectedBrand : localStorage.getItem("supplierBrand"),
  );
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [searchQuery, setSearchQuery] = useState(""); // New search state
  const gifUrl = "/Images/New notification.gif";
  const [tooltip, setTooltip] = useState(null);
  const [stopOrderNotifications, setStopOrderNotifications] = useState([]);
  const [PausedOrders, setPausedOrders] = useState({});
  const [ContinuedOrders, setContinuedOrders] = useState({});
  const [truckCount, setTruckCount] = useState(1);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [newDispatchQty, setNewDispatchQty] = useState("");
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [dispatchRate, setDispatchRate] = useState("");
  const [deductionAmount, setDeductionAmount] = useState(0);
  const [showPaymentPOModal, setShowPaymentPOModal] = useState(false);
  const [paymentPOFiles, setPaymentPOFiles] = useState({
    payment: null,
    po: null,
    utrNumber: null,
  });
  const [showLowBalanceWarning, setShowLowBalanceWarning] = useState(false);
  // Get count context
  const { updateCount } = useCount();
  const [showDelayPopup, setShowDelayPopup] = useState(false);
  const [delayReason, setDelayReason] = useState("");

  // Effect to update brand when selectedBrand prop changes (for admin view)
  useEffect(() => {
    if (adminView) {
      setBrand(selectedBrand);
    }
  }, [adminView, selectedBrand]);

  // Effect to check for adminSelectedBrand in localStorage
  useEffect(() => {
    if (adminView && !selectedBrand) {
      const storedBrand = localStorage.getItem("adminSelectedBrand");
      if (storedBrand) {
        setBrand(storedBrand);
      }
    }
  }, [adminView, selectedBrand]);

  // Update count whenever orders change
  useEffect(() => {
    const pendingOrdersCount = orders.filter(
      (order) => order.status === "Pending",
    ).length;
    updateCount("userOrders", pendingOrdersCount);
  }, [orders, updateCount]);

  const fetchStopOrderNotifications = useCallback(async () => {
    try {
      const db = getDatabase();
      const notificationsRef = ref(db, "supplier_notifications/stop_orders");
      onValue(notificationsRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const notificationsList = [];
          const PausedOrdersMap = {};
          const ContinuedOrdersMap = {};
          Object.keys(data).forEach((userId) => {
            Object.keys(data[userId]).forEach((notificationId) => {
              const notification = data[userId][notificationId];
              // Only include notifications for this brand
              if (!brand || notification.brand === brand) {
                notificationsList.push({
                  id: notificationId,
                  userId,
                  ...notification,
                });
                // Track Paused and Continued orders
                if (
                  notification.title === "Order Paused" ||
                  notification.status === "Paused"
                ) {
                  PausedOrdersMap[notification.orderId] = {
                    timestamp: notification.timestamp,
                    status: "Paused",
                  };
                } else if (
                  notification.title === "Order Continued" ||
                  notification.status === "Continued"
                ) {
                  ContinuedOrdersMap[notification.orderId] = {
                    timestamp: notification.timestamp,
                    status: "Continued",
                  };
                }
              }
            });
          });
          setStopOrderNotifications(notificationsList);
          setPausedOrders(PausedOrdersMap);
          setContinuedOrders(ContinuedOrdersMap);
        }
      });
    } catch (error) {
      //Error fetching stop order notifications
    }
  }, [brand]);

  // Fetch stop order notifications
  useEffect(() => {
    if (brand) {
      fetchStopOrderNotifications();
    }
  }, [brand, fetchStopOrderNotifications]);

  const getIndianTimestamp = () => {
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
    const istTime = new Date(now.getTime() + istOffset);
    return istTime.toISOString().replace("T", " ").replace("Z", "");
  };

  const sendNotification = async (userId, orderId, title, message) => {
    // Generate a unique notification ID using timestamp and random number
    const notificationId = `notification_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    // Use the unique notification ID in the path instead of just the orderId
    const notificationRef = ref(
      db,
      `user_notifications/Order Dispatch/${userId}/${notificationId}`,
    );
    const newNotification = {
      title,
      message,
      orderId, // Store the orderId as a field in the notification
      gifUrl,
      timestamp: getIndianTimestamp(),
      seen: false,
    };
    try {
      await set(notificationRef, newNotification);
    } catch (error) {
      //Error sending notification
    }
  };

  const sendLowBalanceNotification = async (
    userId,
    orderId,
    currentBalance,
    deductionAmount,
    brand,
    grade,
    siteAddress,
  ) => {
    const notificationId = `notification_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const notificationRef = ref(
      db,
      `user_notifications/Low Balance/${userId}/${notificationId}`,
    );
    const newNotification = {
      title: "Low Balance Alert",
      message: `Your current balance of ₹${currentBalance} is insufficient to dispatch ${brand} (${grade}) to ${siteAddress}.Please visit the Order Status page and make a payment of ₹${deductionAmount} to proceed with your order.`,
      orderId,
      gifUrl,
      timestamp: getIndianTimestamp(),
      seen: false,
    };
    try {
      await set(notificationRef, newNotification);
    } catch (error) {
      //Error sending low balance notification
    }
  };

  // ====================== LOG CREDIT / DEBIT TRANSACTION ======================
  const logTransaction = async (
    userUid,
    type,
    amount,
    order,
    extraData = {},
  ) => {
    if (!userUid || !amount) return;

    const transId = `trans_${Date.now()}`;

    const transactionData = {
      type: type, // "credit" or "debit"
      amount: Number(amount),
      brand: order.brand,
      codeID: order.code,
      orderId: order.orderId,
      description:
        extraData.description ||
        (type === "credit" ? "Payment Confirmed" : "Dispatch Done"),
      timestamp: getIndianTimestamp(),
      balanceAfter: null, // Will be updated later if needed
      ...extraData,
    };

    try {
      const updates = {};

      // 1. Update balance in new-code (atomic)
      const codePath = `new-code/${userUid}/codes`;
      // We need to find the correct codeKey first (by codeID)
      // For simplicity, we'll update balance directly if we can find the matching code

      // Log the transaction
      const transPath = `transactions/${userUid}/${transId}`;
      updates[transPath] = transactionData;

      await update(ref(db), updates);
      console.log(`✅ ${type.toUpperCase()} transaction logged successfully`);
    } catch (error) {
      console.error("Failed to log transaction:", error);
    }
  };

  // Helper function to check if an order is Paused
  const isOrderPaused = (orderId) => {
    if (!PausedOrders[orderId]) return false;
    if (ContinuedOrders[orderId]) {
      return (
        new Date(PausedOrders[orderId].timestamp) >
        new Date(ContinuedOrders[orderId].timestamp)
      );
    }
    const pauseTime = new Date(PausedOrders[orderId].timestamp);
    const now = new Date();
    const hoursDiff = (now - pauseTime) / (1000 * 60 * 60);
    return hoursDiff <= 24;
  };

  // Helper function to check if an order was recently Continued
  const isOrderContinued = (orderId) => {
    if (!ContinuedOrders[orderId]) return false;
    // Check if there's a more recent "Paused" status
    if (PausedOrders[orderId]) {
      if (
        new Date(ContinuedOrders[orderId].timestamp) <
        new Date(PausedOrders[orderId].timestamp)
      ) {
        return false;
      }
    }
    // Check if the continue is within the last 24 hours
    const continueTime = new Date(ContinuedOrders[orderId].timestamp);
    const now = new Date();
    const hoursDiff = (now - continueTime) / (1000 * 60 * 60);
    return hoursDiff <= 24;
  };

  // Helper function to generate multiple truck and driver numbers
  const generateMultipleTruckAndDriverInfo = (count) => {
    let result = "";
    for (let i = 1; i <= count; i++) {
      // Generate a random truck number between 1 and 20
      const truckNo = Math.floor(Math.random() * 20) + 1;
      // Generate a random driver number between 100 and 999
      const driverNo = Math.floor(Math.random() * 900) + 100;
      const quantity = Math.floor(Math.random() * 20) + 1;
      result += `Truck No:${truckNo}, Driver No.:${driverNo}, Qty:${quantity}`;
      // Add separator if not the last item
      if (i < count) {
        result += " | ";
      }
    }
    return result;
  };

  const handleInputChange = (orderId, field, value) => {
    // Create a copy of the current updated orders
    const updatedOrdersCopy = { ...updatedOrders };
    // If the field doesn't exist for this order, initialize it
    if (!updatedOrdersCopy[orderId]) {
      updatedOrdersCopy[orderId] = {};
    }
    // Update the specific field with the new value
    updatedOrdersCopy[orderId][field] = value;
    if (field === "rate") {
      setDispatchRate(value);
      // Recalculate deduction amount if dispatch quantity exists
      if (newDispatchQty) {
        let deductionQty = Number.parseFloat(newDispatchQty);
        // If the order is for MT (metric tons), convert to bags (1 MT = 20 bags)
        if (
          currentOrder &&
          currentOrder.quantityType &&
          currentOrder.quantityType.toLowerCase() === "mt"
        ) {
          deductionQty = deductionQty * 20;
        }
        const calculatedDeduction =
          Number.parseFloat(value || "0") * deductionQty;
        setDeductionAmount(calculatedDeduction);
      }
    }
    // If the status is being changed to "Dispatch" and shipping detail is empty,
    // auto-generate truck and driver information
    if (field === "status" && value === "Dispatch") {
      if (
        !updatedOrdersCopy[orderId].shippingDetail ||
        updatedOrdersCopy[orderId].shippingDetail === ""
      ) {
        updatedOrdersCopy[orderId].shippingDetail =
          generateMultipleTruckAndDriverInfo(truckCount);
      }
    }
    // Auto-generate truck and driver info when truck count changes
    if (field === "truckCount") {
      updatedOrdersCopy[orderId].shippingDetail =
        generateMultipleTruckAndDriverInfo(Number.parseInt(value));
    }
    // Update the state
    setUpdatedOrders(updatedOrdersCopy);
  };

  // FIX: Improved fetchOrders function with better real-time updates
  const fetchOrders = useCallback(
    async (selectedBrand) => {
      try {
        const ordersRef = ref(getDatabase(), "orders");
        // Use onValue for real-time updates
        onValue(
          ordersRef,
          (snapshot) => {
            if (snapshot.exists()) {
              const data = snapshot.val();
              // Fetch sites data to get balances
              const sitesRef = ref(getDatabase(), "new-code");
              onValue(
                sitesRef,
                (sitesSnapshot) => {
                  if (sitesSnapshot.exists()) {
                    const sitesData = sitesSnapshot.val();
                    const filteredOrders = [];
                    const companySet = new Set();
                    const today = new Date().toISOString().split("T")[0];
                    // Calculate date from one week ago for filtering completed orders
                    const oneWeekAgo = new Date();
                    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                    const orderMap = new Map();
                    Object.keys(data).forEach((uid) => {
                      Object.keys(data[uid]).forEach((orderId) => {
                        // Skip if already processed with the same data
                        const orderKey = `${uid}-${orderId}`;
                        const order = data[uid][orderId];
                        // Only fetch cement orders - admin will handle steel and machinery
                        const isCementOrder =
                          !order.category ||
                          order.category === "Cement" ||
                          order.category === "Bulker Cement";
                        // For admin view with no brand selected, show all cement orders
                        // For admin view with brand selected, filter by their brand
                        let brandMatch = false;
                        if (adminView && !selectedBrand) {
                          brandMatch = true;
                        } else {
                          brandMatch = order.brand === selectedBrand;
                        }
                        const getLastTransactionTimestamp = (transactions) => {
                          if (!transactions) return null;
                          const txnArray = Object.values(transactions);
                          txnArray.sort(
                            (a, b) =>
                              new Date(b.timestamp) - new Date(a.timestamp),
                          ); // latest first
                          return txnArray.length > 0
                            ? txnArray[0].timestamp
                            : null;
                        };
                        // Only include cement orders that match the selected brand and filter out completed orders older than one week
                        if (isCementOrder && brandMatch) {
                          // Check if order is completed and older than one week - if so, don't include in UI
                          const isCompletedOld =
                            order.completed &&
                            getLastTransactionTimestamp(order.transactions) &&
                            new Date(
                              getLastTransactionTimestamp(order.transactions),
                            ) < oneWeekAgo;
                          if (!isCompletedOld) {
                            // Find balance from new-code structure using the code field
                            let siteBalance = 0;
                            let foundBalanceInDatabase = false;
                            if (
                              order.code &&
                              sitesData &&
                              sitesData[uid] &&
                              sitesData[uid].codes
                            ) {
                              const userCodes = sitesData[uid].codes;
                              // Find matching code by comparing with order.code
                              Object.keys(userCodes).forEach((codeKey) => {
                                const codeData = userCodes[codeKey];
                                if (codeData.codeID === order.code) {
                                  siteBalance = codeData.balance || 0;
                                  foundBalanceInDatabase = true;
                                }
                              });
                            } else if (
                              order.siteAddress &&
                              sitesData &&
                              sitesData[uid] &&
                              sitesData[uid].brands &&
                              sitesData[uid].brands[order.brand]
                            ) {
                              const userSites =
                                sitesData[uid].brands[order.brand].sites;
                              if (userSites) {
                                // Find matching site by address for this specific user
                                Object.keys(userSites).forEach((siteId) => {
                                  const site = userSites[siteId];
                                  if (
                                    site.address === order.siteAddress.address
                                  ) {
                                    siteBalance = site.balance || 0;
                                    foundBalanceInDatabase = true;
                                  }
                                });
                              }
                            }
                            // Get quantity from the new structure if available
                            const quantity =
                              order.quantityData?.total || order.quantity || 0;
                            const quantityType = order.quantityType || "bags";
                            // Calculate dispatch and pending quantities from dispatchTransactions
                            let totalDispatched = 0;
                            let totalPending = quantity;
                            let lastDispatchTransaction = null;
                            if (order.dispatchTransactions) {
                              const transactions = Object.values(
                                order.dispatchTransactions,
                              );
                              // Sort transactions by timestamp (newest first)
                              transactions.sort(
                                (a, b) =>
                                  new Date(b.timestamp) - new Date(a.timestamp),
                              );
                              if (transactions.length > 0) {
                                lastDispatchTransaction = transactions[0];
                                // Calculate total dispatched
                                totalDispatched = transactions.reduce(
                                  (sum, transaction) => {
                                    return (
                                      sum +
                                      (Number.parseInt(transaction.dispatch) ||
                                        0)
                                    );
                                  },
                                  0,
                                );
                                // Use the pending value from the latest transaction instead of calculating
                                totalPending = lastDispatchTransaction.pending
                                  ? Number(lastDispatchTransaction.pending)
                                  : quantity - totalDispatched;
                                if (totalPending < 0) totalPending = 0;
                              }
                            }
                            const orderWithBalance = {
                              orderId,
                              userId: uid,
                              ...order,
                              siteAddress: order.siteAddress
                                ? {
                                    ...order.siteAddress,
                                    transportation_type:
                                      order.siteAddress.transportation_type ||
                                      null,
                                  }
                                : null,
                              quantity: quantity, // Use the total from quantityData if available
                              quantityType: quantityType,
                              isToday:
                                order.timestamp &&
                                order.timestamp.startsWith(today),
                              currentBalance: siteBalance, // This is now correctly fetched from the specific user's data
                              totalDispatched,
                              totalPending,
                              lastDispatchTransaction,
                              payment_status: order.payment_status || "pending", // ADDED: Payment status
                            };
                            filteredOrders.push(orderWithBalance);
                            orderMap.set(orderKey, orderWithBalance);
                            companySet.add(order.companyName);
                          }
                        }
                      });
                    });
                    // Sort orders: Newest orders on top
                    filteredOrders.sort(
                      (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
                    );
                    setOrders(filteredOrders);
                    applyFilters(
                      filteredOrders,
                      selectedCompany,
                      selectedStatus,
                      searchQuery,
                    );
                    setCompanies(Array.from(companySet));
                    setIsLoading(false);
                  }
                },
                { onlyOnce: false }, // FIX: Changed to false to get real-time updates
              );
            } else {
              setError("No orders found for this brand.");
              setIsLoading(false);
            }
          },
          (error) => {
            setError("Error fetching orders: " + error.message);
            setIsLoading(false);
          },
        );
      } catch (err) {
        setError("Error fetching orders: " + err.message);
        setIsLoading(false);
      }
    },
    [adminView, selectedCompany, selectedStatus, searchQuery],
  );

  useEffect(() => {
    if (adminView) {
      // For admin view, fetch orders directly
      fetchOrders(brand);
    } else {
      // For supplier view, check authentication first
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          if (!brand) {
            setError("Brand not selected. Please log in again.");
            setIsLoading(false);
            return;
          }
          fetchOrders(brand);
        } else {
          setError("User not authenticated. Please log in again.");
          setIsLoading(false);
        }
      });
    }
  }, [brand, adminView, fetchOrders]);

  const applyFilters = (allOrders, company, status, search) => {
    let result = [...allOrders];
    // Apply search filter
    if (search && search.trim() !== "") {
      const searchTerm = search.toLowerCase().trim();
      result = result.filter((order) => {
        // Combine address fields into one string
        const fullAddress = order.siteAddress
          ? `${order.siteAddress.address || ""} ${order.siteAddress.state || ""} ${order.siteAddress.pincode || ""}`
              .toLowerCase()
              .replace(/,/g, "") // remove commas
              .replace(/\s+/g, " ") // normalize spaces
          : "";
        // Search in multiple fields including full address
        const searchableFields = [
          order.companyName?.toLowerCase() || "",
          order.brand?.toLowerCase() || "",
          order.grade?.toLowerCase() || "",
          order.orderId?.toLowerCase() || "",
          order.userId?.toLowerCase() || "",
          order.category?.toLowerCase() || "",
          order.status?.toLowerCase() || "",
          order.code?.toLowerCase() || "",
          fullAddress,
        ];
        return searchableFields.some((field) =>
          field.includes(searchTerm.replace(/,/g, "").replace(/\s+/g, " ")),
        );
      });
    }
    // Apply company filter
    if (company) {
      result = result.filter((order) => order.companyName === company);
    }
    // Apply status filter
    if (status) {
      const lowerStatus = status.toLowerCase();
      if (lowerStatus === "Paused") {
        result = result.filter((order) => isOrderPaused(order.orderId));
      } else if (lowerStatus === "Continued") {
        result = result.filter((order) => isOrderContinued(order.orderId));
      } else if (lowerStatus === "completed") {
        result = result.filter((order) => order.completed === true);
      } else {
        result = result.filter(
          (order) => order.status?.toLowerCase() === lowerStatus,
        );
      }
    }
    console.log("Filtered Orders:", result); // Debugging log
    setFilteredOrders(result);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const searchValue = e.target.value;
    setSearchQuery(searchValue);
    applyFilters(orders, selectedCompany, selectedStatus, searchValue);
  };
  const handleCompanyChange = (e) => {
    const company = e.target.value;
    setSelectedCompany(company);
    applyFilters(orders, company, selectedStatus, searchQuery);
  };
  const handleStatusChange = (e) => {
    const status = e.target.value;
    setSelectedStatus(status);
    applyFilters(orders, selectedCompany, status, searchQuery);
  };
  const handleTruckCountChange = (e) => {
    const count = Number.parseInt(e.target.value, 10);
    setTruckCount(count);
    // Auto-generate truck and driver info when count changes
    if (currentOrder) {
      handleInputChange(currentOrder.orderId, "truckCount", count);
      handleInputChange(
        currentOrder.orderId,
        "shippingDetail",
        generateMultipleTruckAndDriverInfo(count),
      );
    }
  };

  const openShippingModal = (order) => {
    setCurrentOrder(order);
    // Initialize the updated order data if not already set
    if (!updatedOrders[order.orderId]) {
      setUpdatedOrders((prev) => ({
        ...prev,
        [order.orderId]: {
          shippingDetail: order.shippingDetail || "",
          status: order.status || "Pending",
        },
      }));
    }
    setShowShippingModal(true);
  };
  const closeShippingModal = () => {
    setShowShippingModal(false);
    setCurrentOrder(null);
    setNewDispatchQty("");
  };

  // Replace the openUpdateModal function with this corrected version that fetches rate from the order
  const openUpdateModal = (order) => {
    setCurrentOrder(order);
    setNewDispatchQty("");
    // Initialize the updated order data if not already set
    if (!updatedOrders[order.orderId]) {
      setUpdatedOrders((prev) => ({
        ...prev,
        [order.orderId]: {
          shippingDetail: order.shippingDetail || "",
          status: order.status || "Pending",
          rate: order.rate || "",
        },
      }));
    }
    // Get rate from the order if available
    if (order.rate) {
      setDispatchRate(order.rate.toString());
      // Calculate deduction amount if dispatch quantity is already set
      if (newDispatchQty) {
        const calculatedDeduction =
          Number.parseFloat(order.rate) * Number.parseFloat(newDispatchQty);
        setDeductionAmount(calculatedDeduction);
      }
    } else {
      setDispatchRate("");
      setDeductionAmount(0);
    }
    setShowUpdateModal(true);
  };
  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    setCurrentOrder(null);
    setNewDispatchQty("");
  };

  const openTransactionsModal = (order) => {
    setCurrentOrder(order);
    setShowTransactionsModal(true);
  };
  const closeTransactionsModal = () => {
    setShowTransactionsModal(false);
    setCurrentOrder(null);
  };
  const resolveFirebaseFileUrl = async (fileValue) => {
    if (!fileValue) return null;

    // If already a full URL
    if (fileValue.startsWith("http")) {
      return fileValue;
    }

    // Otherwise treat it as Firebase Storage path
    const storage = getStorage();
    const fileRef = storageRef(storage, fileValue);
    return await getDownloadURL(fileRef);
  };
  // Add a function to open the Payment/PO modal after the closeTransactionsModal function
  const openPaymentPOModal = async (order) => {
    setCurrentOrder(order);

    try {
      const db = getDatabase();
      const orderRef = ref(db, `orders/${order.userId}/${order.orderId}`);
      const snapshot = await get(orderRef);

      if (!snapshot.exists()) return;

      const orderData = snapshot.val();

      const paymentUrl = await resolveFirebaseFileUrl(orderData.paymentFileUrl);
      const poUrl = await resolveFirebaseFileUrl(orderData.poFileUrl);

      setPaymentPOFiles({
        payment: paymentUrl,
        po: poUrl,
        utrNumber: orderData.utrNumber || null,
      });

      setShowPaymentPOModal(true);
    } catch (error) {
      console.error("Error fetching payment/PO files:", error);
    }
  };
  // Add a function to close the Payment/PO modal after the openPaymentPOModal function
  const closePaymentPOModal = () => {
    setShowPaymentPOModal(false);
    setCurrentOrder(null);
    setPaymentPOFiles({ payment: null, po: null, utrNumber: null });
  };

  useEffect(() => {
    if (dispatchRate && newDispatchQty) {
      let deductionQty = Number.parseFloat(newDispatchQty);
      // If the order is for MT (metric tons), convert to bags (1 MT = 20 bags)
      if (
        currentOrder &&
        currentOrder.quantityType &&
        currentOrder.quantityType.toLowerCase() === "mt"
      ) {
        deductionQty = deductionQty * 20; // 1 MT = 20 bags
      }
      const calculatedDeduction =
        Number.parseFloat(dispatchRate) * deductionQty;
      setDeductionAmount(calculatedDeduction);
    } else {
      setDeductionAmount(0);
    }
  }, [dispatchRate, newDispatchQty, currentOrder]);

  // NEW: Confirm Payment
  // NEW: Confirm Payment + Log Credit Transaction
  const confirmPayment = async (order) => {
    if (order.payment_status === "confirmed") return;

    try {
      const paymentAmount = Number(order.paymentAmount) || 0;

      // 1. Update order payment status
      await update(ref(db, `orders/${order.userId}/${order.orderId}`), {
        payment_status: "confirmed",
        statusUpdatedAt: getIndianTimestamp(),
      });

      // 2. Log Credit Transaction
      await logTransaction(order.userId, "credit", paymentAmount, order, {
        paymentMethod: "utr",
        utrNumber: order.utrNumber || "",
        paymentFileUrl: order.paymentFileUrl || "",
        description: `Payment confirmed via UTR: ${order.utrNumber || "N/A"}`,
      });

      // 3. Send notification to user
      await sendNotification(
        order.userId,
        order.orderId,
        "Payment Confirmed",
        `Your payment of ₹${paymentAmount} has been confirmed. Dispatch can now proceed.`,
      );

      alert("Payment confirmed and credit logged successfully!");
    } catch (e) {
      console.error("Payment confirm failed:", e);
      alert("Failed to confirm payment");
    }
  };

  // NEW: Reject Payment
  const rejectPayment = async (order) => {
    if (order.payment_status === "rejected") return;
    try {
      await update(ref(db, `orders/${order.userId}/${order.orderId}`), {
        payment_status: "rejected",
        statusUpdatedAt: getIndianTimestamp(),
      });
      await sendNotification(
        order.userId,
        order.orderId,
        "Payment Rejected",
        `Your payment proof was rejected. Please upload a valid proof or contact support.`,
      );
    } catch (e) {
      console.error("Payment reject failed:", e);
    }
  };

  const handleSave = async (order) => {
    try {
      // Disable save button
      const saveBtn = document.querySelector(".save-btn");
      if (saveBtn) {
        saveBtn.disabled = true;
        saveBtn.textContent = "Saving...";
      }
      const previousOrder = orders.find((o) => o.orderId === order.orderId);
      const { shippingDetail, ...updatedData } =
        updatedOrders[order.orderId] || {};
      // Check if there are any changes in status or other fields
      const hasStatusChange =
        updatedData?.status && updatedData.status !== previousOrder.status;
      const hasOtherChanges =
        updatedData &&
        Object.keys(updatedData).some(
          (key) => key !== "status" && updatedData[key] !== previousOrder[key],
        );
      const hasNewDispatch = newDispatchQty && newDispatchQty.trim() !== "";
      // For Underloading status, we don't need dispatch quantity or shipping details to be mandatory
      const isUnderloadingUpdate = updatedData?.status === "Underloading";
      // Validation logic - updated to handle Underloading status properly
      if (!isUnderloadingUpdate && hasNewDispatch) {
        // For dispatch updates, validate required fields
        if (
          !newDispatchQty?.trim() || // empty or whitespace
          /^0+$/.test(newDispatchQty.trim()) || // just zeroes
          !updatedOrders[order.orderId]?.shippingDetail?.trim() // missing shipping detail
        ) {
          alert("Order Placed successfully!");
          if (saveBtn) {
            saveBtn.disabled = false;
            saveBtn.textContent = "Save Changes";
          }
          return;
        }
      }
      // If no changes at all, exit early
      if (!hasStatusChange && !hasOtherChanges && !hasNewDispatch) {
        setEditingOrderId(null);
        closeShippingModal();
        closeUpdateModal();
        return;
      }
      const shouldUpdateDate =
        updatedData?.status === "Dispatch" || hasNewDispatch;
      const dispatchDate = shouldUpdateDate
        ? new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
        : previousOrder.dispatchDate;
      // Get current balance from the database first (from new-code node)
      let currentBalance = 0;
      const existingBalance = previousOrder.existingBalance || 0;
      // Fetch the current balance from new-code node
      const db = getDatabase();
      const userCodeRef = ref(db, `new-code/${order.userId}`); // Fetch specific user's data
      // Fetch current balance based on codeID
      await new Promise((resolve) => {
        onValue(
          ref(db, `new-code/${order.userId}/codes`),
          (snapshot) => {
            if (snapshot.exists()) {
              const codesData = snapshot.val();
              let balanceFound = false;
              Object.keys(codesData).forEach((codeKey) => {
                const codeEntry = codesData[codeKey];
                if (
                  codeEntry.codeID?.trim().toLowerCase() ===
                  order.code?.trim().toLowerCase()
                ) {
                  currentBalance = Number(codeEntry.balance) || 0;
                  balanceFound = true;
                }
              });
              if (!balanceFound && hasNewDispatch) {
                alert("No balance found for the given code.");
              }
              currentOrder.currentBalance = currentBalance;
              resolve(true);
            } else {
              if (hasNewDispatch) {
                alert("No code data found for this user.");
              }
              resolve(false);
            }
          },
          { onlyOnce: true },
        );
      });
      // If user has updated the balance in the form, use that value
      if (updatedData?.currentBalance !== undefined) {
        currentBalance = Number(updatedData.currentBalance);
      }
      // Calculate deduction amount based on quantity type (only for dispatch)
      let calculatedDeduction = 0;
      if (hasNewDispatch) {
        const dispatchQty = Number(newDispatchQty);
        let deductionQty = dispatchQty;
        // If the order is for MT (metric tons), convert to bags (1 MT = 20 bags)
        if (order.quantityType && order.quantityType.toLowerCase() === "mt") {
          deductionQty = dispatchQty * 20; // 1 MT = 20 bags
        }
        const currentRate =
          updatedOrders[order.orderId]?.rate || dispatchRate || order.rate;
        // Calculate deduction amount
        calculatedDeduction = Number.parseFloat(currentRate) * deductionQty;
        // Check if current balance is sufficient for the deduction
        if (calculatedDeduction > 0 && calculatedDeduction > currentBalance) {
          // Show low balance warning
          setShowLowBalanceWarning(true);
          // Re-enable save button
          if (saveBtn) {
            saveBtn.disabled = false;
            saveBtn.textContent = "Save Changes";
          }
          return;
        }
      }
      setDeductionAmount(calculatedDeduction);
      // ====================== LOG DEBIT TRANSACTION ======================
     if (hasNewDispatch && calculatedDeduction > 0) {
  const dispatchQty = Number(newDispatchQty);

  const currentRate =
    updatedOrders[order.orderId]?.rate || dispatchRate || order.rate;

  const transactionId = `trans_${Date.now()}`;

  await logTransaction(
  order.userId,
  "debit",
  calculatedDeduction,
  order,
  {
    description: `Dispatch of ${dispatchQty} ${order.quantityType || "bags"}`,
    dispatchQty: dispatchQty,
    dispatchIds: [transactionId],
    rate: currentRate
  },
);
}
      // Update the current balance by subtracting the deduction amount (only for dispatch)
      if (hasNewDispatch && calculatedDeduction > 0) {
        currentBalance = Math.max(0, currentBalance - calculatedDeduction);
      }
      const finalUpdateData = shouldUpdateDate
        ? { ...updatedData }
        : { ...updatedData };

      // Update balance in `new-code` at the code level (only if there's a dispatch)
      if (order.code && hasNewDispatch) {
        const db = getDatabase();
        // Only update the balance for the specific user who placed the order
        const userCodesRef = ref(db, `new-code/${order.userId}/codes`);
        await new Promise((resolve) => {
          onValue(
            userCodesRef,
            (snapshot) => {
              if (snapshot.exists()) {
                const codesData = snapshot.val();
                let balanceUpdated = false;
                // Find the matching code by codeID
                Object.keys(codesData).forEach((codeKey) => {
                  const codeData = codesData[codeKey];
                  if (codeData.codeID === order.code) {
                    // Update balance at the code level
                    const balanceRef = ref(
                      db,
                      `new-code/${order.userId}/codes/${codeKey}/balance`,
                    );
                    set(balanceRef, currentBalance);
                    balanceUpdated = true;
                  }
                });
                resolve(balanceUpdated);
              } else {
                resolve(false);
              }
            },
            { onlyOnce: true },
          );
        });
      }
      if (updatedOrders[currentOrder.orderId]?.status === "Delayed") {
        if (!delayReason.trim()) {
          alert("Please enter delay reason");
          return;
        }

        const delayRef = ref(
          db,
          `orders/${currentOrder.userId}/${currentOrder.orderId}/delay`,
        );

        await set(delayRef, delayReason);

        // 🔔 CREATE USER NOTIFICATION (same child nodes as dispatch/payment)
        const notificationRef = push(
          ref(db, `user_notifications/Order Delay/${currentOrder.userId}`),
        );

        await set(notificationRef, {
          gifUrl: "/Images/New notification.gif",
          message: `Order to "${currentOrder.siteAddress?.address || currentOrder.siteAddress}" delayed due to "${delayReason}"`,
          orderId: currentOrder.orderId,
          seen: false,
          timestamp: getIndianTimestamp(),
          title: "Order Delayed",
        });
      }
      // Handle dispatch
      if (hasNewDispatch) {
        const dispatchQty = Number(newDispatchQty);
        if (dispatchQty > order.totalPending) {
          setError(
            `Dispatch quantity cannot exceed pending quantity (${order.totalPending} ${order.quantityType})`,
          );
          return;
        }
        const pendingQty = order.totalPending - dispatchQty;
        const transactionId = `trans${Date.now()}`;
        const transaction = {
          dispatch: dispatchQty,
          pending: pendingQty,
          timestamp: getIndianTimestamp(),
          shippingDetail:
            updatedOrders[order.orderId]?.shippingDetail ||
            order.shippingDetail ||
            "",
        };
        const dispatchRef = ref(
          getDatabase(),
          `orders/${order.userId}/${order.orderId}/dispatchTransactions/${transactionId}`,
        );
        await set(dispatchRef, transaction);
        // Update existingBalance to match the current balance from new-code
        const existingBalanceRef = ref(
          getDatabase(),
          `orders/${order.userId}/${order.orderId}/existingBalance`,
        );
        await set(existingBalanceRef, currentBalance);
        const statusRef = ref(
          getDatabase(),
          `orders/${order.userId}/${order.orderId}/status`,
        );
        if (updatedData?.status) {
          await set(statusRef, updatedData.status);
          finalUpdateData.status = updatedData.status;
          await set(
            ref(db, `orders/${order.userId}/${order.orderId}/statusUpdatedAt`),
            getIndianTimestamp(),
          );
        } else if (order.status !== "Dispatch") {
          await set(statusRef, "Dispatch");
          finalUpdateData.status = "Dispatch";
          await set(
            ref(db, `orders/${order.userId}/${order.orderId}/statusUpdatedAt`),
            getIndianTimestamp(),
          );
        }
        if (pendingQty === 0) {
          const completedRef = ref(
            getDatabase(),
            `orders/${order.userId}/${order.orderId}/completed`,
          );
          await set(completedRef, true);
          await set(statusRef, "Completed");
          finalUpdateData.status = "Completed";
          await set(
            ref(db, `orders/${order.userId}/${order.orderId}/statusUpdatedAt`),
            getIndianTimestamp(),
          );
          await sendNotification(
            order.userId,
            order.orderId,
            "Order Completed",
            `Your dispatch of ${dispatchQty} ${order.quantityType} for ${order.brand} ${order.grade} at ${
              order.siteAddress?.address || order.siteAddress
            } has been marked as the final dispatch. Your order is now complete. Thank you for ordering with us.`,
          );
        }
      }

      // Handle status update without new dispatch (including Underloading)
      if (hasStatusChange || hasOtherChanges) {
        const orderRef = ref(
          getDatabase(),
          `orders/${order.userId}/${order.orderId}`,
        );
        await update(orderRef, finalUpdateData);
        if (hasStatusChange) {
          await set(
            ref(db, `orders/${order.userId}/${order.orderId}/statusUpdatedAt`),
            getIndianTimestamp(),
          );
        }
        // Update existingBalance to match the current balance from new-code (only if dispatch happened)
        if (hasNewDispatch) {
          const existingBalanceRef = ref(
            getDatabase(),
            `orders/${order.userId}/${order.orderId}/existingBalance`,
          );
          await set(existingBalanceRef, currentBalance);
        }
        // Send appropriate notifications based on status
        if (finalUpdateData.status === "Dispatch") {
          await sendNotification(
            order.userId,
            order.orderId,
            "Order Dispatch",
            `Your order for ${order.brand} ${order.grade} at ${
              order.siteAddress?.address || order.siteAddress
            } has been dispatched and will be delivered soon.`,
          );
        } else if (finalUpdateData.status === "Underloading") {
          await sendNotification(
            order.userId,
            order.orderId,
            "Order Underloading",
            `Your order for ${order.brand} ${order.grade} at ${
              order.siteAddress?.address || order.siteAddress
            } is currently under loading and will be dispatched shortly.`,
          );
        }
      }
      // Reset states after success
      setEditingOrderId(null);
      setNewDispatchQty("");
      setShowLowBalanceWarning(false);
      setUpdatedOrders((prev) => {
        const newUpdatedOrders = { ...prev };
        delete newUpdatedOrders[order.orderId];
        return newUpdatedOrders;
      });
      // Show success message
      const successMessage = document.createElement("div");
      successMessage.textContent = "Order updated successfully!";
      // Center of screen
      successMessage.style.position = "fixed";
      successMessage.style.top = "50%";
      successMessage.style.left = "50%";
      successMessage.style.transform = "translate(-50%, -50%)";
      successMessage.style.background = "#4CAF50";
      successMessage.style.color = "white";
      successMessage.style.padding = "15px 30px";
      successMessage.style.borderRadius = "10px";
      successMessage.style.boxShadow = "0 2px 10px rgba(0,0,0,0.3)";
      successMessage.style.zIndex = "9999";
      successMessage.style.fontSize = "18px";
      successMessage.style.fontWeight = "bold";
      successMessage.style.textAlign = "center";
      document.body.appendChild(successMessage);
      // auto remove after 2 seconds
      setTimeout(() => successMessage.remove(), 2000);
      closeShippingModal();
      closeUpdateModal();
    } catch (err) {
      //Error updating order
      setError("Failed to update the order. Please try again.");
    } finally {
      // Re-enable save button
      const saveBtn = document.querySelector(".save-btn");
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = "Save Changes";
      }
    }
  };

  // Add a helper function to check if payment or PO files exist after the getCategoryIcon function
  const hasPaymentOrPOFiles = (order) => {
    return (
      (order.paymentFileUrl && order.paymentFileUrl.trim() !== "") ||
      (order.poFileUrl && order.poFileUrl.trim() !== "") ||
      (order.utrNumber && order.utrNumber.trim() !== "")
    );
  };

  // Helper function to check if fields should be disabled based on status - FIXED
  const shouldDisableFields = (status) => {
    // Disable fields for both "Pending" and "Underloading" statuses
    return status === "Pending" || status === "Underloading";
  };

  if (isLoading)
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading orders...</p>
      </div>
    );
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="orders-container">
      <h1>
        Cement Orders
        <InfoIcon text="This section provides a complete overview of your received orders, including pending and dispatched details. You can view essential order information such as company name, brand, grade, quantity, and payment status. Additionally, you can update the Dispatch Quantity, Shipping Detail, and Dispatch Date to ensure accurate tracking of shipments. The shipping details include truck numbers and driver numbers for efficient logistics management." />
      </h1>
      <div className="orders-controls">
        <div className="control-item">
          <label htmlFor="search-input">Search</label>
          <input
            id="search-input"
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by company, site address, brand, order ID..."
          />
        </div>

        <div className="control-item">
          <label htmlFor="company-select">Company</label>
          <select
            id="company-select"
            value={selectedCompany}
            onChange={handleCompanyChange}
          >
            <option value="">All Companies</option>
            {companies.map((company, index) => (
              <option key={index} value={company}>
                {company}
              </option>
            ))}
          </select>
        </div>

        <div className="control-item">
          <label htmlFor="status-select">Status</label>
          <select
            id="status-select"
            value={selectedStatus}
            onChange={handleStatusChange}
          >
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Underloading">Underloading</option>
            <option value="Dispatch">Dispatch</option>
            <option value="Completed">Completed</option>
            <option value="Paused">Paused</option>
            <option value="Continued">Continued</option>
          </select>
        </div>
      </div>
      {filteredOrders.length === 0 ? (
        <div className="no-orders">
          <p>No orders found for the selected filters.</p>
        </div>
      ) : (
        <div className="scrollable-table-container">
          <table className="orders-table">
            <thead>
              <tr className="table-header">
                <th>Order Dates</th>
                <th>Company Name</th>
                <th>Product Details</th>
                <th>Quantity</th>
                <th>Site Address</th>
                <th>Payment Amount</th>
                <th>Status</th>
                <th>Dispatch/Pending</th>
                <th>Current Balance</th>
                {/* <th>Shipping Detail</th> */}
                <th>Payment Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order, index) => {
                const isUpdated = order.status && order.status !== "Pending"; // If status is updated
                const shouldHighlight = !isUpdated; // If still Pending, highlight
                const isPaused = isOrderPaused(order.orderId);
                const isContinued = isOrderContinued(order.orderId);
                const isPending = order.status === "Pending";
                const isBulkerCement = order.category === "Bulker Cement";
                let rowClassName = "";
                if (isPaused) {
                  rowClassName = "Paused-order";
                } else if (isContinued) {
                  rowClassName = "Continued-order";
                } else if (shouldHighlight) {
                  rowClassName = "highlight-flash";
                } else {
                  rowClassName = index % 2 === 0 ? "even-row" : "odd-row";
                }
                return (
                  <tr key={order.orderId} className={rowClassName}>
                    <td>
                      {new Date(order.timestamp).toLocaleDateString("en-IN", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      })}
                    </td>
                    <td>{order.companyName}</td>
                    <td>
                      {/* Display product details */}
                      <div>
                        <strong>Brand:</strong> {order.brand}
                        <br />
                        <strong>Grade:</strong> {order.grade}
                        <br />
                        <strong>Type:</strong> {order.category || "Cement"}
                      </div>
                    </td>
                    <td>
                      {order.quantity} {order.quantityType || "bags"}
                    </td>
                    <td>
                      {order.siteAddress ? (
                        <>
                          <div>{order.siteAddress.address}</div>

                          <div>
                            {order.siteAddress.state},{" "}
                            {order.siteAddress.pincode}
                          </div>

                          {order.siteAddress.transportation_type && (
                            <div style={{ marginTop: "6px" }}>
                              <strong>Transportation Type:</strong>{" "}
                              {order.siteAddress.transportation_type}
                            </div>
                          )}
                        </>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td>{order.paymentAmount}</td>
                    <td>
                      <span
                        className={`status-badge ${
                          isPaused
                            ? "status-Paused"
                            : order.status?.toLowerCase() || "pending"
                        }`}
                      >
                        {order.status || "Pending"} {isPaused && "(Paused)"}
                      </span>
                    </td>
                    <td>
                      <div className="dispatch-pending-info">
                        <div className="dispatch-info">
                          <span className="dispatch-label">Dispatch:</span>
                          <span className="dispatch-value">
                            {order.totalDispatched || 0}
                          </span>
                        </div>
                        <div className="pending-info">
                          <span className="pending-label">Pending:</span>
                          <span className="pending-value">
                            {order.totalPending}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="current-balance">
                        {order.currentBalance !== undefined
                          ? order.currentBalance
                          : "Loading..."}
                      </span>
                    </td>
                    {/* <td>
                     <div className="shipping-detail">
                       {isPending ? (
                         <span className="no-shipping">updated soon</span>
                       ) : order.lastDispatchTransaction?.shippingDetail ? (
                         <div className="shipping-info">
                           {order.lastDispatchTransaction.shippingDetail.split("|").map((info, idx) => (
                             <div key={idx} className="truck-info">

                               <span>{info.trim()}</span>
                             </div>
                           ))}
                         </div>
                       ) : (
                         <span className="no-shipping">Not specified</span>
                       )}
                     </div>
                   </td> */}

                    {/* PAYMENT CONFIRM/REJECT */}
                    <td>
                      {order.payment_status === "confirmed" ? (
                        <span className="status-badge status-confirmed">
                          Confirmed
                        </span>
                      ) : order.payment_status === "rejected" ? (
                        <span className="status-badge status-rejected">
                          Rejected
                        </span>
                      ) : (
                        <div className="payment-actions">
                          <button
                            className="btn-confirm"
                            onClick={() => confirmPayment(order)}
                            title="Confirm payment"
                          >
                            <FaCheckCircle />
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() => rejectPayment(order)}
                            title="Reject payment"
                          >
                            <FaBan />
                          </button>
                        </div>
                      )}
                    </td>

                    <td>
                      <div className="action-buttons-container">
                        <button
                          className="edit-button"
                          onClick={() => openUpdateModal(order)}
                          disabled={
                            isPaused || order.payment_status !== "confirmed"
                          }
                          title={
                            order.payment_status !== "confirmed"
                              ? "Payment not confirmed"
                              : isPaused
                                ? "Order is paused"
                                : ""
                          }
                        >
                          <FaPencilAlt className="edit-icon" /> Update
                        </button>
                        {order.dispatchTransactions &&
                          Object.keys(order.dispatchTransactions).length >
                            0 && (
                            <button
                              className="view-transactions-button"
                              onClick={() => openTransactionsModal(order)}
                            >
                              <FaTruck className="transactions-icon" />{" "}
                              Dispatches
                            </button>
                          )}
                        {hasPaymentOrPOFiles(order) && (
                          <button
                            className="view-payment-po-button"
                            onClick={() => openPaymentPOModal(order)}
                          >
                            <FaInfoCircle className="payment-po-icon" />{" "}
                            Payment/PO & UTR
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Shipping Modal */}
      {showShippingModal && currentOrder && (
        <div className="modal-overlay">
          <div className="shipping-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Shipping Details</h3>
              <button className="close-modal-btn" onClick={closeShippingModal}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-content">
              <div className="order-info">
                <p>
                  <strong>Company:</strong> {currentOrder.companyName}
                </p>
                <p>
                  <strong>Type:</strong> {currentOrder.category || "Cement"}
                </p>
                <p>
                  <strong>Brand:</strong> {currentOrder.brand}
                </p>
                <p>
                  <strong>Grade:</strong> {currentOrder.grade}
                </p>
                <p>
                  <strong>Quantity:</strong> {currentOrder.quantity}{" "}
                  {currentOrder.quantityType || "bags"}
                </p>
              </div>
              <div className="form-group">
                <label>Number of Trucks:</label>
                <select
                  value={truckCount}
                  onChange={handleTruckCountChange}
                  className="truck-count-select"
                  disabled={shouldDisableFields(
                    updatedOrders[currentOrder.orderId]?.status ||
                      currentOrder.status,
                  )}
                >
                  {[...Array(20)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Shipping Details:</label>
                <textarea
                  value={
                    updatedOrders[currentOrder.orderId]?.shippingDetail ||
                    currentOrder.shippingDetail ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChange(
                      currentOrder.orderId,
                      "shippingDetail",
                      e.target.value,
                    )
                  }
                  className="shipping-textarea"
                  placeholder="Enter shipping details"
                  rows={4}
                  disabled={shouldDisableFields(
                    updatedOrders[currentOrder.orderId]?.status ||
                      currentOrder.status,
                  )}
                />
              </div>
              <div className="modal-actions">
                <div className="action-buttons">
                  <div className="button-divider"></div>
                  <button
                    className="save-btn"
                    onClick={() => handleSave(currentOrder)}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Order Modal */}
      {showUpdateModal && currentOrder && (
        <div className="modal-overlay">
          <div className="shipping-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Update Order</h3>
              <button className="close-modal-btn" onClick={closeUpdateModal}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-content">
              <div className="order-info">
                <p>
                  <strong>Company:</strong> {currentOrder.companyName}
                </p>
                <p>
                  <strong>Type:</strong> {currentOrder.category || "Cement"}
                </p>
                <p>
                  <strong>Brand:</strong> {currentOrder.brand}
                </p>
                <p>
                  <strong>Grade:</strong> {currentOrder.grade}
                </p>
                <p>
                  <strong>Quantity:</strong> {currentOrder.quantity}{" "}
                  {currentOrder.quantityType || "bags"}
                </p>
                <p>
                  <strong>Total Dispatched:</strong>{" "}
                  {currentOrder.totalDispatched || 0}{" "}
                  {currentOrder.quantityType || "bags"}
                </p>
                <p>
                  <strong>Pending:</strong> {currentOrder.totalPending}{" "}
                  {currentOrder.quantityType || "bags"}
                </p>
                <p>
                  <strong>Payment Amount:</strong>{" "}
                  {currentOrder.paymentAmount || 0}
                </p>
                <p>
                  <strong>Your Last Balance:</strong>{" "}
                  {currentOrder.existingBalance || 0}
                </p>
              </div>
              {/* Previous Transactions */}
              {currentOrder.dispatchTransactions &&
                Object.keys(currentOrder.dispatchTransactions).length > 0 && (
                  <div className="previous-transactions">
                    <h4>Previous Transactions</h4>
                    <div className="transactions-list">
                      {Object.entries(currentOrder.dispatchTransactions)
                        .sort(
                          ([, a], [, b]) =>
                            new Date(b.timestamp) - new Date(a.timestamp),
                        )
                        .map(([transId, transaction], index) => {
                          const transactionDate = new Date(
                            transaction.timestamp,
                          );
                          const formattedDate =
                            transactionDate.toLocaleDateString();
                          const formattedTime =
                            transactionDate.toLocaleTimeString();
                          return (
                            <div key={transId} className="transaction-item">
                              <div className="transaction-header">
                                <div className="transaction-title">
                                  <span className="transaction-id">
                                    {transId}
                                  </span>
                                  {index === 0 && (
                                    <span className="latest-badge">Latest</span>
                                  )}
                                </div>
                                <span className="transaction-date">
                                  {formattedDate} at {formattedTime}
                                </span>
                              </div>
                              <div className="transaction-details">
                                <div className="transaction-qty">
                                  <span className="qty-item">
                                    <span className="qty-label">
                                      Dispatched:
                                    </span>
                                    <span className="qty-value dispatch-value">
                                      {transaction.dispatch}{" "}
                                      {currentOrder.quantityType}
                                    </span>
                                  </span>
                                  <span className="qty-item">
                                    <span className="qty-label">Pending:</span>
                                    <span className="qty-value pending-value">
                                      {transaction.pending}{" "}
                                      {currentOrder.quantityType}
                                    </span>
                                  </span>
                                </div>
                                {transaction.shippingDetail && (
                                  <div className="transaction-shipping">
                                    <strong>Shipping:</strong>{" "}
                                    {transaction.shippingDetail}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              <div className="form-group">
                <label>Status:</label>
                <select
                  value={
                    updatedOrders[currentOrder.orderId]?.status ||
                    currentOrder.status ||
                    "Pending"
                  }
                  onChange={(e) => {
                    const value = e.target.value;

                    handleInputChange(currentOrder.orderId, "status", value);

                    if (value === "Delayed") {
                      setShowDelayPopup(true);
                    }
                  }}
                  className="status-select"
                >
                  <option value="Pending">Pending</option>
                  <option value="Delayed">Delayed</option>
                  <option value="Underloading">Underloading</option>
                  <option value="Dispatch">Dispatch</option>
                </select>
              </div>
              <div className="form-group">
                <label>
                  New Dispatch Quantity:{" "}
                  <span className="required-field">*</span>
                </label>
                <input
                  value={newDispatchQty}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow numbers and ensure it doesn't exceed pending quantity
                    if (
                      value === "" ||
                      (/^\d+$/.test(value) &&
                        Number.parseInt(value) <= currentOrder.totalPending)
                    ) {
                      setNewDispatchQty(value);
                      // Update deduction amount when dispatch quantity changes
                      if (dispatchRate) {
                        const calculatedDeduction =
                          Number.parseFloat(dispatchRate) *
                          Number.parseInt(value || "0");
                        setDeductionAmount(calculatedDeduction);
                      }
                    }
                  }}
                  className="dispatch-qty-input"
                  placeholder={`Enter dispatch quantity (max: ${currentOrder.totalPending})`}
                  onClick={(e) => e.target.select()}
                  disabled={shouldDisableFields(
                    updatedOrders[currentOrder.orderId]?.status ||
                      currentOrder.status,
                  )}
                />
                <span className="quantity-type-label">
                  {currentOrder.quantityType || "bags"}
                </span>
              </div>
              <div className="balance-row">
                <div className="field-section">
                  <label>Rate:</label>
                  <input
                    value={dispatchRate}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow numbers and decimal points
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        handleInputChange(currentOrder.orderId, "rate", value);
                      }
                    }}
                    className="dispatch-rate-input"
                    placeholder="Enter rate"
                    disabled={shouldDisableFields(
                      updatedOrders[currentOrder.orderId]?.status ||
                        currentOrder.status,
                    )}
                    type="number"
                    step="0.01"
                    min="0"
                    onWheel={(e) => e.preventDefault()}
                  />
                </div>
                <div className="field-section">
                  <label>Current Balance:</label>
                  <input
                    value={currentOrder.currentBalance || 0}
                    className="read-only-input"
                    placeholder="Current balance"
                    disabled={true}
                  />
                </div>
                <div className="field-section">
                  <label>Deduction Amount:</label>
                  <input
                    value={deductionAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setDeductionAmount(Number.parseFloat(value) || 0);
                      }
                    }}
                    className="deduction-amount-input"
                    placeholder="Deduction amount"
                    disabled={shouldDisableFields(
                      updatedOrders[currentOrder.orderId]?.status ||
                        currentOrder.status,
                    )}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Number of Trucks:</label>
                <select
                  value={truckCount}
                  onChange={handleTruckCountChange}
                  className="truck-count-select"
                  disabled={shouldDisableFields(
                    updatedOrders[currentOrder.orderId]?.status ||
                      currentOrder.status,
                  )}
                >
                  {[
                    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17,
                    18, 19, 20,
                  ].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>
                  Shipping Details: <span className="required-field">*</span>
                </label>
                <textarea
                  value={
                    updatedOrders[currentOrder.orderId]?.shippingDetail ||
                    currentOrder.shippingDetail ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChange(
                      currentOrder.orderId,
                      "shippingDetail",
                      e.target.value,
                    )
                  }
                  className="shipping-textarea"
                  placeholder="Enter shipping details"
                  rows={4}
                  disabled={shouldDisableFields(
                    updatedOrders[currentOrder.orderId]?.status ||
                      currentOrder.status,
                  )}
                />
              </div>
              <div className="modal-actions">
                <div className="action-buttons">
                  <div className="button-divider"></div>
                  <button
                    className="save-btn"
                    onClick={() => handleSave(currentOrder)}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDelayPopup && (
        <div className="modal-overlay">
          <div className="delay-popup" onClick={(e) => e.stopPropagation()}>
            <h3>Reason for Delay</h3>

            <textarea
              value={delayReason}
              onChange={(e) => setDelayReason(e.target.value)}
              placeholder="Enter delay reason"
              rows={4}
            />

            <div className="popup-actions">
              <button
                onClick={() => {
                  setShowDelayPopup(false);
                }}
              >
                OK
              </button>

              <button
                onClick={() => {
                  setDelayReason("");
                  setShowDelayPopup(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transactions History Modal */}
      {showTransactionsModal && currentOrder && (
        <div className="modal-overlay" onClick={closeTransactionsModal}>
          <div
            className="transactions-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Transaction History</h3>
              <button
                className="close-modal-btn"
                onClick={closeTransactionsModal}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-content">
              <div className="order-info">
                <div className="order-header">
                  <h4>
                    {currentOrder.brand} {currentOrder.grade} -{" "}
                    {currentOrder.companyName}
                  </h4>
                  <span className="order-date">
                    Order Date: {currentOrder.timestamp}
                  </span>
                </div>
                <div className="order-details">
                  <div className="order-detail-item">
                    <span className="detail-label">Total Quantity:</span>
                    <span className="detail-value">
                      {currentOrder.quantity}{" "}
                      {currentOrder.quantityType || "bags"}
                    </span>
                  </div>
                  <div className="order-detail-item">
                    <span className="detail-label">Total Dispatched:</span>
                    <span className="detail-value">
                      {currentOrder.totalDispatched || 0}{" "}
                      {currentOrder.quantityType || "bags"}
                    </span>
                  </div>
                  <div className="order-detail-item">
                    <span className="detail-label">Pending:</span>
                    <span className="detail-value">
                      {currentOrder.totalPending}{" "}
                      {currentOrder.quantityType || "bags"}
                    </span>
                  </div>
                  <div className="order-detail-item">
                    <span className="detail-label">Status:</span>
                    <span
                      className={`status-badge ${currentOrder.status?.toLowerCase() || "pending"}`}
                    >
                      {currentOrder.status || "Pending"}
                    </span>
                  </div>
                </div>
              </div>
              {currentOrder.dispatchTransactions &&
              Object.keys(currentOrder.dispatchTransactions).length > 0 ? (
                <div className="transactions-timeline">
                  {Object.entries(currentOrder.dispatchTransactions)
                    .sort(
                      ([, a], [, b]) =>
                        new Date(b.timestamp) - new Date(a.timestamp),
                    )
                    .map(([transId, transaction], index) => {
                      const transactionDate = new Date(transaction.timestamp);
                      const formattedDate =
                        transactionDate.toLocaleDateString();
                      const formattedTime =
                        transactionDate.toLocaleTimeString();
                      return (
                        <div key={transId} className="timeline-item">
                          <div className="timeline-marker">
                            <div className="timeline-icon">
                              <FaTruck />
                            </div>
                            <div className="timeline-line"></div>
                          </div>
                          <div className="timeline-content">
                            <div className="transaction-card">
                              <div className="transaction-header">
                                <div className="transaction-title">
                                  <span className="transaction-id">
                                    {transId}
                                  </span>
                                  <span
                                    className={
                                      index === 0 ? "latest-badge" : ""
                                    }
                                  >
                                    {index === 0 ? "Latest" : ""}
                                  </span>
                                </div>
                                <div className="transaction-date">
                                  <div className="date">{formattedDate}</div>
                                  <div className="time">{formattedTime}</div>
                                </div>
                              </div>
                              <div className="transaction-body">
                                <div className="transaction-quantities">
                                  <div className="quantity-item">
                                    <span className="quantity-label">
                                      Dispatched:
                                    </span>
                                    <span className="quantity-value dispatch-value">
                                      {transaction.dispatch}{" "}
                                      {currentOrder.quantityType}
                                    </span>
                                  </div>
                                  <div className="quantity-item">
                                    <span className="quantity-label">
                                      Pending:
                                    </span>
                                    <span className="quantity-value pending-value">
                                      {transaction.pending}{" "}
                                      {currentOrder.quantityType}
                                    </span>
                                  </div>
                                </div>
                                {transaction.shippingDetail && (
                                  <div className="shipping-details">
                                    <div className="shipping-header">
                                      Shipping Details:
                                    </div>
                                    <div className="shipping-trucks">
                                      {transaction.shippingDetail
                                        .split("|")
                                        .map((info, idx) => (
                                          <div key={idx} className="truck-info">
                                            <FaTruck className="truck-icon" />
                                            <span>{info.trim()}</span>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="no-transactions">
                  <p>No transaction history available for this order.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment/PO Files Modal */}
      {showPaymentPOModal && currentOrder && (
        <div className="modal-overlay" onClick={closePaymentPOModal}>
          <div
            className="payment-po-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Client Files</h3>
              <button className="close-modal-btn" onClick={closePaymentPOModal}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-content">
              <div className="order-info">
                <div className="order-header">
                  <h4>
                    {currentOrder.brand} {currentOrder.grade} -{" "}
                    {currentOrder.companyName}
                  </h4>
                  <span className="order-date">
                    Order Date: {currentOrder.timestamp}
                  </span>
                </div>
              </div>
              <div className="files-container">
                <h4>Files</h4>
                {paymentPOFiles.payment && (
                  <div className="file-item">
                    <div className="file-label">Client Payment Proof</div>
                    <div className="file-actions">
                      <a
                        href={paymentPOFiles.payment}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="file-action-btn"
                      >
                        <FaEye /> View
                      </a>
                    </div>
                  </div>
                )}
                {paymentPOFiles.po && (
                  <div className="file-item">
                    <div className="file-label">Client Purchase Order</div>
                    <div className="file-actions">
                      <a
                        href={paymentPOFiles.po}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="file-action-btn"
                      >
                        <FaEye /> View
                      </a>
                    </div>
                  </div>
                )}
                {paymentPOFiles.utrNumber && (
                  <div className="file-item">
                    <div className="file-label">UTR Number</div>
                    <div className="file-value">{paymentPOFiles.utrNumber}</div>
                  </div>
                )}
                {!paymentPOFiles.payment &&
                  !paymentPOFiles.po &&
                  !paymentPOFiles.utrNumber && (
                    <div className="no-files">
                      <p>No files available for this order.</p>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Low Balance Warning Modal */}
      {showLowBalanceWarning && currentOrder && (
        <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div
            className="low-balance-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="warning-title">Insufficient Balance</h3>
              <button
                className="close-modal-btn"
                onClick={() => setShowLowBalanceWarning(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-content">
              <div className="warning-message">
                Client's current balance is not enough to continue dispatch
              </div>
              <div className="balance-details">
                <p>
                  <strong>Current Balance:</strong>{" "}
                  {currentOrder.currentBalance || 0}
                </p>
                <p>
                  <strong>Required Amount:</strong> {deductionAmount}
                </p>
                <p>
                  <strong>Shortage:</strong>{" "}
                  {Math.max(
                    0,
                    deductionAmount - (currentOrder.currentBalance || 0),
                  )}
                </p>
              </div>
              <div className="modal-actions">
                <button
                  className="notify-customer-btn"
                  onClick={() => {
                    sendLowBalanceNotification(
                      currentOrder.userId,
                      currentOrder.orderId,
                      currentOrder.currentBalance || 0,
                      deductionAmount,
                      currentOrder.brand,
                      currentOrder.grade,
                      currentOrder.siteAddress?.address || "your site",
                    );
                    setShowLowBalanceWarning(false);
                    // Show notification sent message
                    const successMessage = document.createElement("div");
                    successMessage.className = "success-alert";
                    successMessage.textContent =
                      "Customer has been notified about low balance!";
                    document.body.appendChild(successMessage);
                    // Remove success message after 2 seconds
                    setTimeout(() => {
                      if (document.body.contains(successMessage)) {
                        document.body.removeChild(successMessage);
                      }
                    }, 2000);
                  }}
                >
                  Notify Customer
                </button>
                <button
                  className="cancel-btn"
                  onClick={() => setShowLowBalanceWarning(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserOrders;
